import { ChangeDetectionStrategy, Component, ElementRef, Input, Renderer2, ViewChild } from '@angular/core';
import { NgAfterViewInit } from '@cikrf/gas-utils/decorators';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { sizeMapHelper } from '../../core/sizes/size-map-helper';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

type Sizes = 'lg' | 'md' | 'xm' | 'xs';
type Variant = 'circular' | 'default';

/** @see https://www.figma.com/file/yqBeq1MiGfpdN62SvmA8tg/RTLABS_GAS_DS_1?node-id=2408%3A33733 */
@UntilDestroy()
@Component({
  selector: 'gas-progress-bar',
  templateUrl: './progress-bar.component.html',
  styleUrls: ['./progress-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GasProgressBarComponent {
  @ViewChild('progressBar')
  public progressBarRef: ElementRef;

  @NgAfterViewInit()
  public init$!: Observable<void>;

  @Input()
  public set variant(variant: Variant) {
    this.variantSubject$.next(variant);
  }

  @Input()
  public set size(size: Sizes) {
    this.sizeSubject$.next(size);
  }

  @Input()
  public percentType: 'percentUp' | 'percentEnd';

  @Input()
  public value: number;

  @Input()
  public title = 'Loading...';

  public heightSize = {
    lg: '12px',
    md: '8px',
    xm: '4px',
    xs: '2px',
  };

  private sizeSubject$ = new BehaviorSubject<Sizes>(null);
  private variantSubject$ = new BehaviorSubject<Variant>(null);

  constructor(
      private renderer: Renderer2,
  ) {
    this.sizeSubject$.asObservable()
      .pipe(
        switchMap((size: Sizes) => this.init$.pipe(map(() => size))),
        untilDestroyed(this),
      )
      .subscribe((size: Sizes) => {
        this.renderer.setStyle(this.progressBarRef.nativeElement, 'height', this.heightSize[sizeMapHelper.getCorrectSize(size)]);
      });

    this.variantSubject$.asObservable()
      .pipe(
        switchMap((variant: Variant) => this.init$.pipe(map(() => variant))),
        untilDestroyed(this),
      )
      .subscribe((variant: Variant) => {
        if (variant === 'circular') {
          this.renderer.setStyle(this.progressBarRef.nativeElement, 'border-radius', '8px');
        } else {
          this.renderer.removeStyle(this.progressBarRef.nativeElement, 'border-radius');
        }
      });
  }

  /**
   * проверка, что this.percentType === 'percentUp'
   * для отображения процентов над прогресс-баром
   */
  public isPercentUp(): boolean {
    return this.percentType === 'percentUp';
  }

  /**
   * проверка, что this.percentType === 'percentEnd'
   * для отображения процентов после прогресс-бара
   */
  public isPercentEnd(): boolean {
    return this.percentType === 'percentEnd';
  }
}
